import { reactive } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { getProficiencyBadgeProps } from "@components/utils/proficiencyBadgeView";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";

let instance = null;

export function useViewState() {
  if (instance) return instance;
  const filter = useFilters();
  const state = reactive({
    filters: {
      values: getDefaultFiltersForKey("readinessChecklist"),
      searchText: "",
      showFilterDialog: false,
      key: "readinessChecklist",
      clearSelection: "",
      statusOptions: [],
      filterGroups: [
        {
          label: "Status",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Not Yet Passed", value: "not_yet_passed" },
            { label: "Only Failed", value: "failed" },
            { label: "Only Not Yet Evaluated", value: "not_yet_evaluated" },
            { label: "Only Reset", value: "reset" },
            { label: "Only Not Applicable", value: "not_applicable" },
            { label: "Only Passed", value: "passed" },
          ],
        },
      ],
    },
    loading: {
      table: false,
    },
    table: {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
  });

  const getEmployeeCardProps = (data) => {
    return {
      firstName: data?.employee?.first_name,
      lastName: data?.employee?.last_name,
      position: data?.employee?.title,
      photoUrl: data?.employee?.avatar,
      availability: data?.employee?.availability,
      showAvailability: true,
      nameUrl: `data?.employees?/${data?.employee?.id}/pathtoreadiness`,
      showLinkUnderline: false,
      hideAvailabilitySelector: data?.employee?.primary_positions?.length > 0 || false,
      isAllocated: data?.employee?.primary_positions?.length > 0 || false,
    };
  };

  const applyFilters = (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    state.filters.showFilterDialog = false;
  };

  const removeFilters = (removedFilters) => {
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilters;
  };

  const getOrgObjectCardProps = (obj) => {
    return {
      code: obj.code,
      name: obj.name,
      showIcon: false,
      removeUnderlineToName: true,
    };
  };

  const getIssuedProps = (obj) => {
    return {
      name: obj?.badge?.granted_by?.name,
      picture: obj?.badge?.granted_by?.picture,
      userType: obj?.badge?.granted_by?.userType,
      displayDate: formatDateToDisplay(obj?.badge?.granted_on),
    };
  };

  const getCheckPointCounterProps = (obj) => {
    return {
      countApprenticeCheckpoints:
        obj.active_apprentice_checkpoints || obj.count_apprentice_checkpoints,
      countProfessionalCheckpoints:
        obj.active_professional_checkpoints || obj.count_professional_checkpoints,
      countCoachCheckpoints: obj.active_coach_checkpoints || obj.count_coach_checkpoints,
      countMasterCheckpoints: obj.active_master_checkpoints || obj.count_master_checkpoints,
      activeCheckpointsCount: obj.active_checkpoints_count,
      draftCheckpointsCount: obj.draft_checkpoints_count,
      draftApprenticeCheckpoints: obj.draft_apprentice_checkpoints || 0,
      draftProfessionalCheckpoints: obj.draft_professional_checkpoints || 0,
      draftCoachCheckpoints: obj.draft_coach_checkpoints || 0,
      draftMasterCheckpoints: obj.draft_master_checkpoints || 0,
      countApprentice: obj.active_apprentice_checkpoints || obj.checkpoint?.apprentice,
      countProfessional: obj.active_professional_checkpoints || obj.checkpoint?.professional,
      countCoach: obj.active_coach_checkpoints || obj.checkpoint?.coach,
      countMaster: obj.active_master_checkpoints || obj.checkpoint?.master,
    };
  };

  const getRemovedProps = (obj) => {
    return {
      name: obj?.badge?.removed_by?.name,
      picture: obj?.badge?.removed_by?.picture,
      userType: obj?.badge?.removed_by?.userType,
      displayDate: formatDateToDisplay(obj?.badge?.removed_on),
    };
  };

  const formatDateToDisplay = (dateString) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const date = new Date(dateString);

    if (isNaN(date)) return "Invalid Date";

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert to 12-hour format

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const getInspectionStatusBadgeProps = (obj) => {
    return {
      inspected_by_avatar: obj?.check_point?.inspected_by_avatar,
      inspected_by: obj?.check_point?.inspected_by,
      inspected_on: obj?.check_point?.inspected_on,
      checkpoint_status: obj?.check_point?.checkpoint_status,
      reason: obj?.evaluation_instruction?.evaluation_reason,
    };
  };

  const getEvaluationInstructionCard = (obj) => {
    return {
      rowId: obj.evaluation_instruction.rowId,
      status: obj.evaluation_instruction.status,
      level: obj.evaluation_instruction.level,
      criteria: obj.evaluation_instruction.criteria,
      instructions: obj.evaluation_instruction.instructions,
      failed_by: obj.evaluation_instruction.failed_by,
      failed_on: obj.evaluation_instruction.failed_on,
      failed_reason: obj.evaluation_instruction.failed_reason,
      reset_by: obj.evaluation_instruction.reset_by,
      reset_on: obj.evaluation_instruction.reset_on,
      reset_reason: obj.evaluation_instruction.reset_reason
        ? obj.evaluation_instruction.reset_reason
        : "",
    };
  };

  const updateFilter = (filterName, value) => {
    if (filterName in state.filters) {
      state.filters[filterName] = value;
    }
  };

  const clearFilters = () => {
    // ER-11195: "Clear all" restores the documented defaults from the central
    // registry instead of resetting the whole filters object to a legacy shape.
    state.filters.values = filter.resetToDefaults(state.filters.key);
    state.filters.showFilterDialog = false;
  };

  const setLoading = (key, value) => {
    if (key in state.loading) {
      state.loading[key] = value;
    }
  };

  instance = {
    state,
    updateFilter,
    clearFilters,
    setLoading,
    getOrgObjectCardProps,
    getCheckPointCounterProps,
    applyFilters,
    removeFilters,
    getIssuedProps,
    getEmployeeCardProps,
    getProficiencyBadgeProps,
    getRemovedProps,
    getInspectionStatusBadgeProps,
    getEvaluationInstructionCard,
  };
  return instance;
}

export function resetReadinessChecklistViewState() {
  instance = null;
}
